import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';
import { HttpAsociacionEmpleadoService } from 'src/app/infrastructure/adapters/asociacion-empleado/http-asociacion-empleado.service';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { resolveSearchErrorMessage } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { AsociacionCatalogoConfig, FiltroCascadaNivel, OpcionFiltro } from './asociacion-catalogo.config';
import { SeleccionarItemDialogComponent } from './seleccionar-item-dialog.component';

/**
 * Componente genérico de asociación catálogo↔empleado — un tab del diálogo
 * "Asociaciones" de Modificar Empleado (HU1259149, Fase 1 §1.6 / Fase 3).
 * Una instancia por catálogo, configurada por {@link AsociacionCatalogoConfig}.
 *
 * <p>Escenarios cubiertos: consultar (paginado, keyset), agregar (lote vía
 * {@link SeleccionarItemDialogComponent}), rechazar duplicado (mensaje de
 * lote con el prefijo exacto del backend), modificar (Servicios/Zonas,
 * inputs planos — ver {@code camposEditables} en el config) y dar de baja
 * (individual y multi-selección en Especialidades). Baja siempre es UPDATE
 * lógico en el backend (hallazgo 1, HU1259149.md) — este componente nunca
 * llama DELETE.</p>
 */
@Component({
  selector: 'app-asociacion-catalogo',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DatePicker,
    TooltipModule,
    BusquedaPaginadorComponent,
    SeleccionarItemDialogComponent
  ],
  template: `
    <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
      <h3 class="text-base font-medium text-gray-700 dark:text-gray-200">{{ config.tituloLista }}</h3>
      <div class="flex gap-2">
        @if (config.permiteBajaMultiple && seleccionados.length > 0) {
          <p-button
            [label]="'Dar de baja (' + seleccionados.length + ')'"
            icon="pi pi-trash"
            severity="danger"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="desasociarSeleccionados()"
          ></p-button>
        }
        <p-button label="Agregar" icon="pi pi-plus" styleClass="min-h-[44px]" (onClick)="abrirPicker()"></p-button>
      </div>
    </div>

    @if (mensaje()) {
      <div
        class="p-3 mb-3 rounded whitespace-pre-line text-sm"
        [ngClass]="{
          'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200': mensajeTipo() === 'error',
          'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200': mensajeTipo() === 'warning',
          'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200': mensajeTipo() === 'success'
        }"
      >
        {{ mensaje() }}
      </div>
    }

    <!-- Panel de filtros en cascada (hallazgo M-5) — solo Zonas lo configura. -->
    @if (config.filtrosCascada?.length) {
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
        @for (nivel of config.filtrosCascada ?? []; track nivel.campo) {
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">{{ nivel.label }}</label>
            <p-select
              [options]="opcionesDe(nivel)"
              optionLabel="descripcion"
              optionValue="codigo"
              [showClear]="true"
              [disabled]="nivelDeshabilitado(nivel)"
              placeholder="Todos"
              styleClass="w-full"
              [ngModel]="valorDe(nivel)"
              (ngModelChange)="onFiltroCambio(nivel, $event)"
            ></p-select>
          </div>
        }
      </div>
    }

    <p-table
      [value]="items()"
      [loading]="cargando()"
      [(selection)]="seleccionados"
      [dataKey]="'nmconsecutivo'"
      [scrollable]="true"
      [responsiveLayout]="'scroll'"
      [rowHover]="true"
      scrollHeight="280px"
      [tableStyle]="{ 'min-width': '100%' }"
      styleClass="p-datatable-sm p-datatable-gridlines p-datatable-striped"
    >
      <ng-template pTemplate="header">
        <tr>
          @if (config.permiteBajaMultiple) {
            <th style="width: 3rem"><p-tableHeaderCheckbox></p-tableHeaderCheckbox></th>
          }
          @for (columna of config.columnasLista; track columna.campo) {
            <th>{{ columna.header }}</th>
          }
          <th style="width: 6rem">Acciones</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-fila>
        <tr>
          @if (config.permiteBajaMultiple) {
            <td><p-tableCheckbox [value]="fila"></p-tableCheckbox></td>
          }
          @for (columna of config.columnasLista; track columna.campo) {
            <td [attr.title]="fila[columna.campo]">{{ fila[columna.campo] }}</td>
          }
          <td class="whitespace-nowrap">
            @if (config.permiteModificar) {
              <button
                pButton
                type="button"
                icon="pi pi-pencil"
                class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Modificar"
                (click)="abrirModificar(fila)"
              ></button>
            }
            @if (!config.permiteBajaMultiple) {
              <button
                pButton
                type="button"
                icon="pi pi-trash"
                severity="danger"
                class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Dar de baja"
                (click)="desasociar(fila)"
              ></button>
            }
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="config.columnasLista.length + 2" class="text-center text-gray-500 py-4">
            {{ cargando() ? 'Cargando...' : 'No hay ' + config.tituloLista.toLowerCase() + ' asociados.' }}
          </td>
        </tr>
      </ng-template>
    </p-table>

    <app-busqueda-paginador
      [currentPage]="currentPage()"
      [totalPages]="totalPages()"
      [hasPrevious]="hasPrevious()"
      [hasNext]="hasNext()"
      [pageSizeOptions]="pageSizeOptions"
      [selectedPageSize]="pageSize()"
      (pageSizeChange)="onPageSizeChange($event)"
      (first)="irPrimeraPagina()"
      (previous)="paginaAnterior()"
      (next)="paginaSiguiente()"
      (last)="irUltimaPagina()"
    ></app-busqueda-paginador>

    <app-seleccionar-item-dialog
      [visible]="mostrarPicker()"
      (visibleChange)="mostrarPicker.set($event)"
      [config]="config"
      [nmconsecutivoCliente]="nmconsecutivoCliente ?? null"
      (seleccionConfirmada)="onSeleccionConfirmada($event)"
    ></app-seleccionar-item-dialog>

    @if (mostrarModificar()) {
      <p-dialog
        header="Modificar"
        [(visible)]="modificarVisibleInterno"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '28rem' }"
      >
        @for (campo of config.camposEditables ?? []; track campo.campo) {
          <div class="mb-3">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">{{ campo.label }}</label>
            @if (campo.tipo === 'numero') {
              <p-inputNumber
                [(ngModel)]="cambiosModificar[campo.campo]"
                styleClass="w-full"
                [useGrouping]="false"
                [min]="campo.min ?? null"
                [max]="campo.max ?? null"
              ></p-inputNumber>
            } @else if (campo.tipo === 'fecha') {
              <p-datepicker
                [(ngModel)]="cambiosModificar[campo.campo]"
                dateFormat="yy/mm/dd"
                [showIcon]="true"
                appendTo="body"
                styleClass="w-full"
              ></p-datepicker>
            } @else {
              <input
                pInputText
                class="w-full"
                maxlength="100"
                [ngModel]="cambiosModificar[campo.campo]"
                (ngModelChange)="cambiosModificar[campo.campo] = sanitizeText($event)"
              />
            }
          </div>
        }
        <div class="flex justify-end gap-2 mt-2">
          <p-button label="Cancelar" [outlined]="true" styleClass="min-h-[44px]" (onClick)="mostrarModificar.set(false)"></p-button>
          <p-button label="Guardar" [loading]="guardandoModificar()" styleClass="min-h-[44px]" (onClick)="guardarModificar()"></p-button>
        </div>
      </p-dialog>
    }
  `
})
export class AsociacionCatalogoComponent implements OnChanges {

  @Input({ required: true }) config!: AsociacionCatalogoConfig<any, any>;
  @Input({ required: true }) nmconsecutivoEmpleado!: number;
  @Input() nmconsecutivoCliente: number | null = null;
  /** Solo para Unidades de Negocio/Categorías Cliente — viaja en el body de asociarLote (Fase 1/2 §1.2). */
  @Input() nombreComercialCliente: string | null = null;

  protected readonly sanitizeText = sanitizeText;
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  protected readonly items = signal<any[]>([]);
  protected readonly cargando = signal(false);
  protected readonly mensaje = signal('');
  protected readonly mensajeTipo = signal<'info' | 'warning' | 'error' | 'success'>('info');
  protected readonly hasNext = signal(false);
  protected readonly hasPrevious = signal(false);
  protected readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);

  /**
   * Paginación real (frontend-girop §17/§8 — auditoría de tablas): antes
   * `currentPage`/`totalPages` se hardcodeaban a 1/1 y "primera"/"última
   * página" quedaban permanentemente inertes. `totalElements` ahora lo
   * expone el backend (`countByEmpleado`, antes sin usar); el cálculo de
   * `totalPages` sigue el mismo patrón que `administrar-empleados.facade.ts`
   * (HU1259115, precedente de esta HU).
   */
  protected readonly currentPage = signal(1);
  protected readonly totalElements = signal(0);
  protected readonly totalPages = computed(() => Math.ceil(this.totalElements() / this.pageSize()) || 1);

  /** Valor elegido por nivel del panel de filtros en cascada (hallazgo M-5). */
  protected readonly valoresFiltro = signal<Record<string, string>>({});
  /** Opciones cargadas por nivel; un nivel sin entrada aún no se ha pedido. */
  protected readonly opcionesFiltro = signal<Record<string, OpcionFiltro[]>>({});

  protected readonly mostrarPicker = signal(false);
  protected readonly mostrarModificar = signal(false);
  protected readonly guardandoModificar = signal(false);

  protected seleccionados: any[] = [];
  protected cambiosModificar: Record<string, unknown> = {};

  private filaEnEdicion: any = null;
  /** Cursores reales de la última página cargada (keyset) — ver `cargar()`. */
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;
  /**
   * (cursor, direction) que produjeron la página actualmente mostrada —
   * distinto de `nextCursorActual`/`previousCursorActual` (que apuntan a la
   * página siguiente o anterior). Se usa para refrescar la página actual
   * tras una mutación sin avanzar de página (hallazgo real detectado al
   * corregir la paginación: antes `cargar(false)` reutilizaba el cursor de
   * "siguiente página", así que agregar/editar/dar de baja en la página 1
   * saltaba silenciosamente a la página 2 en la siguiente recarga).
   */
  private cursorPaginaActual: string | null = null;
  private direccionPaginaActual: 'NEXT' | 'PREVIOUS' = 'NEXT';

  constructor(private readonly asociacionService: HttpAsociacionEmpleadoService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nmconsecutivoEmpleado'] || changes['config']) {
      this.currentPage.set(1);
      this.nextCursorActual = null;
      this.previousCursorActual = null;
      this.valoresFiltro.set({});
      this.opcionesFiltro.set({});
      this.cargar();
      this.cargarPrimerNivelCascada();
    }
  }

  /** El primer nivel (sin dependencias) se llena al abrir el tab, igual que el legado. */
  private async cargarPrimerNivelCascada(): Promise<void> {
    const primero = this.config.filtrosCascada?.[0];
    if (primero) {
      await this.cargarOpciones(primero);
    }
  }

  /** Opciones ya cargadas de un nivel; vacío mientras no se hayan pedido. */
  protected opcionesDe(nivel: FiltroCascadaNivel): OpcionFiltro[] {
    return this.opcionesFiltro()[nivel.campo] ?? [];
  }

  /** Valor elegido de un nivel; null = "Todos". */
  protected valorDe(nivel: FiltroCascadaNivel): string | null {
    return this.valoresFiltro()[nivel.campo] ?? null;
  }

  /** Un nivel está deshabilitado mientras falte alguno de los campos que requiere. */
  protected nivelDeshabilitado(nivel: FiltroCascadaNivel): boolean {
    const valores = this.valoresFiltro();
    return (nivel.requiere ?? []).some(campo => !valores[campo]);
  }

  /**
   * Al cambiar un nivel: se limpian los niveles posteriores (valor y opciones),
   * se cargan las opciones del siguiente y se recarga el listado desde la
   * primera página — el mismo encadenamiento de los {@code addChangeHandler}
   * del legado.
   */
  protected async onFiltroCambio(nivel: FiltroCascadaNivel, valor: string | null): Promise<void> {
    const niveles = this.config.filtrosCascada ?? [];
    const indice = niveles.findIndex(n => n.campo === nivel.campo);

    const valores = { ...this.valoresFiltro() };
    const opciones = { ...this.opcionesFiltro() };
    if (valor) {
      valores[nivel.campo] = `${valor}`;
    } else {
      delete valores[nivel.campo];
    }
    for (const posterior of niveles.slice(indice + 1)) {
      delete valores[posterior.campo];
      delete opciones[posterior.campo];
    }
    this.valoresFiltro.set(valores);
    this.opcionesFiltro.set(opciones);

    const siguiente = niveles[indice + 1];
    if (siguiente && !this.nivelDeshabilitado(siguiente)) {
      await this.cargarOpciones(siguiente);
    }

    this.currentPage.set(1);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
    await this.cargar();
  }

  private async cargarOpciones(nivel: FiltroCascadaNivel): Promise<void> {
    const valores = this.valoresFiltro();
    const params: Record<string, string> = {};
    for (const campo of nivel.paramsDesde ?? []) {
      if (valores[campo]) {
        params[campo] = valores[campo];
      }
    }
    try {
      const opciones = await this.asociacionService.listarOpcionesFiltro<OpcionFiltro>(
        this.nmconsecutivoEmpleado, this.config.recursoAsociacion, nivel.recurso, params
      );
      this.opcionesFiltro.set({ ...this.opcionesFiltro(), [nivel.campo]: opciones });
    } catch {
      // Un fallo al llenar un combo no debe romper el listado: el nivel queda
      // vacío ("Todos") y el usuario sigue viendo sus zonas asociadas.
      this.opcionesFiltro.set({ ...this.opcionesFiltro(), [nivel.campo]: [] });
    }
  }

  get modificarVisibleInterno(): boolean {
    return this.mostrarModificar();
  }

  set modificarVisibleInterno(value: boolean) {
    this.mostrarModificar.set(value);
  }

  /**
   * @param actualizarMensajeVacio false cuando esta recarga es un "refresh"
   * silencioso tras una mutación (agregar/modificar/dar de baja) que ya fijó
   * su propio mensaje de éxito/warning — evita que `cargar()` lo pise con el
   * mensaje de "sin resultados" o lo limpie a `''` (bug real detectado por
   * `asociacion-catalogo.component.spec.ts`: sin este flag, el mensaje de
   * éxito desaparecía en el mismo ciclo en que se mostraba).
   * @param direction NEXT (por defecto) o PREVIOUS — PREVIOUS sin cursor
   * (`cursorOverride === null` y sin página previa cargada) equivale a "ir a
   * la última página" en el backend (keyset), igual que
   * `acceso-datos-oracle` §7 y `AseguradoraRepository`.
   * @param cursorOverride cursor explícito a usar; si se omite, se usa el
   * cursor correspondiente a `direction` de la última página cargada.
   */
  async cargar(
    actualizarMensajeVacio = true,
    direction: 'NEXT' | 'PREVIOUS' = 'NEXT',
    cursorOverride?: string | null
  ): Promise<void> {
    this.cargando.set(true);
    try {
      const cursor = cursorOverride !== undefined
        ? cursorOverride
        : (direction === 'NEXT' ? this.nextCursorActual : this.previousCursorActual);
      const page = await this.asociacionService.listar<any>(
        this.nmconsecutivoEmpleado, this.config.recursoAsociacion, this.pageSize(), cursor ?? undefined, direction,
        this.valoresFiltro()
      );
      this.items.set(page.content);
      this.hasNext.set(page.hasNext);
      this.hasPrevious.set(page.hasPrevious);
      this.totalElements.set(page.totalElements ?? 0);
      this.nextCursorActual = page.nextCursor;
      this.previousCursorActual = page.previousCursor;
      this.cursorPaginaActual = cursor ?? null;
      this.direccionPaginaActual = direction;

      // Ir a la última página (PREVIOUS sin cursor): no hay forma de saber el
      // número real de página desde el backend keyset, así que se estima
      // desde totalElements — mismo cálculo que `administrar-empleados.facade.ts`.
      if (direction === 'PREVIOUS' && cursor == null) {
        const size = this.pageSize();
        const total = this.totalElements();
        this.currentPage.set(size > 0 && total > 0 ? Math.ceil(total / size) : 1);
      }

      if (actualizarMensajeVacio) {
        if (page.content.length === 0) {
          this.mensaje.set('No se recuperó información con los parámetros ingresados.');
          this.mensajeTipo.set('warning');
        } else {
          this.mensaje.set('');
        }
      }
    } catch (error) {
      this.mensaje.set(resolveSearchErrorMessage(error, 'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.'));
      this.mensajeTipo.set('error');
      this.items.set([]);
    } finally {
      this.cargando.set(false);
    }
  }

  /**
   * Refresca la página actualmente visible tras una mutación (agregar,
   * modificar, dar de baja) sin avanzar de página — usa el mismo
   * (cursor, direction) con el que se cargó, no el de la página siguiente.
   */
  private refrescarPaginaActual(): Promise<void> {
    return this.cargar(false, this.direccionPaginaActual, this.cursorPaginaActual);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
    this.cargar(true, 'NEXT', null);
  }

  irPrimeraPagina(): void {
    this.currentPage.set(1);
    this.cargar(true, 'NEXT', null);
  }

  irUltimaPagina(): void {
    this.cargar(true, 'PREVIOUS', null);
  }

  paginaSiguiente(): void {
    if (!this.hasNext()) {
      return;
    }
    this.currentPage.update(p => p + 1);
    this.cargar(true, 'NEXT');
  }

  paginaAnterior(): void {
    if (!this.hasPrevious()) {
      return;
    }
    this.currentPage.update(p => Math.max(1, p - 1));
    this.cargar(true, 'PREVIOUS');
  }

  abrirPicker(): void {
    this.mostrarPicker.set(true);
  }

  async onSeleccionConfirmada(evento: { items: any[]; valoresAlta: Record<string, unknown> }): Promise<void> {
    const seleccionados = evento.items;
    if (seleccionados.length === 0) {
      return;
    }
    const body = this.config.construirBodyAsociar(seleccionados, this.nmconsecutivoCliente, {
      dsnombreComercialCliente: this.nombreComercialCliente,
      // Campos obligatorios del diálogo de agregar (p. ej. "Fecha Inicio" de
      // Servicios) — hallazgo C-1; se normalizan a ISO yyyy-MM-dd.
      ...this.normalizarValoresAlta(evento.valoresAlta)
    });
    try {
      const resultado = await this.asociacionService.asociarLote<{ totalCreados: number; mensajeDuplicados?: string | null }>(
        this.nmconsecutivoEmpleado, this.config.recursoAsociacion, body
      );
      this.componerMensajeLote(resultado);
      await this.refrescarPaginaActual();
    } catch (error) {
      this.mensaje.set(resolveSearchErrorMessage(error, 'No fue posible asociar los registros seleccionados.'));
      this.mensajeTipo.set('error');
    }
  }

  /** Las fechas viajan al backend como `yyyy-MM-dd` (`LocalDate`), no como `Date` de JS. */
  private normalizarValoresAlta(valores: Record<string, unknown>): Record<string, unknown> {
    const normalizados: Record<string, unknown> = {};
    for (const [clave, valor] of Object.entries(valores ?? {})) {
      normalizados[clave] = valor instanceof Date ? this.aIsoFecha(valor) : valor;
    }
    return normalizados;
  }

  private aIsoFecha(fecha: Date): string {
    const mes = `${fecha.getMonth() + 1}`.padStart(2, '0');
    const dia = `${fecha.getDate()}`.padStart(2, '0');
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  private componerMensajeLote(resultado: { totalCreados: number; mensajeDuplicados?: string | null }): void {
    const huboCreados = resultado.totalCreados > 0;
    const huboDuplicados = this.config.validaDuplicado && !!resultado.mensajeDuplicados;

    if (huboCreados && !huboDuplicados) {
      this.mensaje.set('Los datos se almacenaron correctamente.');
      this.mensajeTipo.set('success');
    } else if (huboCreados && huboDuplicados) {
      this.mensaje.set(`Los datos se almacenaron correctamente.\n${resultado.mensajeDuplicados}`);
      this.mensajeTipo.set('success');
    } else if (!huboCreados && huboDuplicados) {
      this.mensaje.set(resultado.mensajeDuplicados ?? '');
      this.mensajeTipo.set('warning');
    } else {
      this.mensaje.set('');
    }
  }

  abrirModificar(fila: any): void {
    this.filaEnEdicion = fila;
    this.cambiosModificar = {};
    for (const campo of this.config.camposEditables ?? []) {
      const valor = fila[campo.campo];
      // p-datepicker trabaja con Date; el backend entrega yyyy-MM-dd.
      this.cambiosModificar[campo.campo] =
        campo.tipo === 'fecha' && valor ? new Date(`${valor}T00:00:00`) : valor;
    }
    this.mostrarModificar.set(true);
  }

  async guardarModificar(): Promise<void> {
    if (!this.config.construirBodyModificar) {
      return;
    }
    this.guardandoModificar.set(true);
    try {
      const body = this.config.construirBodyModificar(
        this.filaEnEdicion, this.normalizarValoresAlta(this.cambiosModificar));
      await this.asociacionService.modificar(this.nmconsecutivoEmpleado, this.config.recursoAsociacion, this.filaEnEdicion.nmconsecutivo, body);
      this.mensaje.set('Los datos se actualizaron correctamente.');
      this.mensajeTipo.set('success');
      this.mostrarModificar.set(false);
      await this.refrescarPaginaActual();
    } catch (error) {
      this.mensaje.set(resolveSearchErrorMessage(error, 'No fue posible actualizar el registro.'));
      this.mensajeTipo.set('error');
    } finally {
      this.guardandoModificar.set(false);
    }
  }

  async desasociar(fila: any): Promise<void> {
    try {
      await this.asociacionService.desasociar(this.nmconsecutivoEmpleado, this.config.recursoAsociacion, fila.nmconsecutivo);
      this.mensaje.set('Los datos se eliminaron correctamente.');
      this.mensajeTipo.set('success');
      await this.refrescarPaginaActual();
    } catch (error) {
      this.mensaje.set(resolveSearchErrorMessage(error, 'No fue posible dar de baja el registro.'));
      this.mensajeTipo.set('error');
    }
  }

  async desasociarSeleccionados(): Promise<void> {
    const pendientes = [...this.seleccionados];
    for (const fila of pendientes) {
      try {
        await this.asociacionService.desasociar(this.nmconsecutivoEmpleado, this.config.recursoAsociacion, fila.nmconsecutivo);
      } catch (error) {
        this.mensaje.set(resolveSearchErrorMessage(error, 'No fue posible dar de baja alguno de los registros seleccionados.'));
        this.mensajeTipo.set('error');
      }
    }
    this.seleccionados = [];
    this.mensaje.set('Los datos se eliminaron correctamente.');
    this.mensajeTipo.set('success');
    await this.refrescarPaginaActual();
  }
}
